import React from 'react';
import { Page } from '../../layout';
import { useQuery, useReactiveVar } from '@apollo/client';
import { userPrivileges, userTier } from '../../../apollo/cache';
import ResponsiveTabMenu from '../../shared/components/ResponsiveTabMenu';
import { Navigate } from 'react-router-dom';
import useUriFragment from './../../shared/hooks/useUriFragment';
import FdaReportManagement from './FdaReportManagement';
import OrganizationReportManagement from './organization/OrganizationReportManagement';
import { getUnitSearchForReportManagement } from '../../shared/services/getUnitSearchPageUrlService';
import { useTabExistCheck } from '../../shared/hooks/useTabExistCheck';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { getActiveTabDisplayName } from '../../shared/services/getActiveTabDisplayNameService';
import { canUserAccessSeveralOrganization } from '../../shared/services/userAccessibilityService';
import useOrgUnitIdFromURL from '../../shared/hooks/useOrgUnitIdFromURL';
import ApolloError from '../../shared/components/ApolloError';
import GET_ORG_UNIT_INFO from '../../shared/queries/getOrgUnitInfoQuery';
import { getPageTitle } from '../../shared/services/pageTitleService';
import { getDataNotFoundPage } from '../../shared/services/getDataNotFoundPageService';
import OrgUnitBreadcrumbAnnotation from '../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';

const ReportManagementPage = () => {
  const orgUnitId = useOrgUnitIdFromURL();

  const privileges = useReactiveVar(userPrivileges);
  const tier = useReactiveVar(userTier);

  const tabs = getTabs();
  const [activeTab, setActiveTab] = useUriFragment(tabs);

  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);
  const breadcrumbPathItems = [
    { name: 'Report Management', url: getUnitSearchForReportManagement(privileges, orgUnitId) },
    { name: activeTabDisplayName, url: '' }
  ];

  useTabExistCheck(tabs, activeTab);

  const handleTabChange = type => {
    setActiveTab(type);
  };

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([orgUnitName, activeTabDisplayName]);
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, renderedOrgUnit]
    : [renderedOrgUnit];

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='report-management'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />

      <ResponsiveTabMenu
        activeTab={activeTab}
        tabs={tabs}
        onChange={newType => handleTabChange(newType)}
      />
      <div>{getActiceTabContent(orgUnitId, activeTab)}</div>
    </Page>
  );
};

const getTabs = () => {
  const tabs = [
    {
      value: 'FDA',
      displayName: 'FDA'
    },
    {
      value: 'organization',
      displayName: 'Organization'
    }
  ];

  return tabs;
};

const getActiceTabContent = (orgUnitId, tabType) => {
  switch (tabType) {
    case 'FDA':
      return <FdaReportManagement orgUnitId={orgUnitId} />;
    case 'organization':
      return <OrganizationReportManagement orgUnitId={orgUnitId} />;
    default:
      return '';
  }
};

export default ReportManagementPage;
